import { Outlet } from 'react-router';

import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { BannerAds } from '#/components/BannerAds/BannerAds';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { AcceptCookiesContainer } from '#/containers/AcceptCookiesContainer';
import { InviteAcceptedToastContainer } from '#/containers/InviteAcceptedToast/InviteAcceptedToastContainer';
import { SignInOrUpReminderContainer } from '#/containers/SignInOrUpReminder/SignInOrUpReminderContainer';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { combinedServerComponentMatchesHandle } from '#/horizon/utils/combined-router-matches-handle';

import type { Route } from './+types/Layout';
import { LayoutHeader } from './Header/LayoutHeader';
import {
  LayoutBaseVariantProgressBarHolder,
  LayoutChildrenHolder,
  LayoutHolder,
  LayoutMobileSearchPageFooterPadding,
} from './Layout.styled';
import type { LayoutHandleProps } from './LayoutHandleProps';
import { LayoutUseAdManager } from './LayoutUseAdManager';
import { PageFooter } from './PageFooter/PageFooter';

export async function loader({ request, url, context }: Route.LoaderArgs) {
  const { user } = await apiIsomorphicFetchUserEnvironment({
    request,
    context,
  })();

  const { serverContext } = getServerRouterContext(context);

  return {
    user,
    pathname: url.pathname,
    locale: serverContext.locale,
    showAnniversaryBanner: serverContext.showAnniversaryBanner,
  };
}

export function ServerComponent({
  matches,
  loaderData: { user, pathname, locale, showAnniversaryBanner },
}: Route.ServerComponentProps) {
  const {
    noAds: noAdsHandle,
    darkBackground,
    hideFooterDivider: hideFooterDividerHandle,
    isSearchPage = false,
    variant = 'default',
    urlByLocale,
    fallbackUrlByLocaleToCurrent,
  } = combinedServerComponentMatchesHandle<LayoutHandleProps>(matches);

  const isLoggedOut = !user;

  const noAds =
    noAdsHandle === 'no-ads-if-logged-out' ? isLoggedOut : Boolean(noAdsHandle);

  const hideFooterDivider =
    hideFooterDividerHandle === 'hide-footer-divider-if-logged-out'
      ? isLoggedOut
      : Boolean(hideFooterDividerHandle);

  return (
    <LayoutHolder data-dark-background={darkBackground ? true : undefined}>
      {variant !== 'base' && <BannerAds />}

      {variant === 'base' ? (
        <LayoutBaseVariantProgressBarHolder>
          <RouteLoadingProgressBar />
        </LayoutBaseVariantProgressBarHolder>
      ) : (
        <LayoutHeader showAnniversaryBanner={showAnniversaryBanner} />
      )}

      <LayoutChildrenHolder data-qa-id="layout-content">
        <Outlet />
      </LayoutChildrenHolder>

      {variant !== 'base' && (
        <>
          <PageFooter
            locale={locale}
            urlByLocale={
              urlByLocale ??
              (fallbackUrlByLocaleToCurrent
                ? { en: pathname, es: pathname, pt: pathname }
                : undefined)
            }
            showDivider={!hideFooterDivider}
          />

          {isSearchPage && <LayoutMobileSearchPageFooterPadding />}
        </>
      )}

      <LayoutUseAdManager noAds={noAds} />
      <AcceptCookiesContainer />
      <SignInOrUpReminderContainer />
      <InviteAcceptedToastContainer />
    </LayoutHolder>
  );
}
